import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackPromoReveal, trackPromoCopy } from '../../../utils/promoStats';
import { openPartnerLink } from '../../../utils/openPartnerLink';
import { getPromoActionLabel, getPromoBadgeLabel } from '../../../utils/promoUi';
import { readPersistedStores } from '../../../utils/storeSeed';

interface StorePromo {
  id: string;
  code: string;
  discount: string;
  description: string;
  expiryDate: string;
  isActive: boolean;
  isTopDeal?: boolean;
  image?: string;
  partnerUrl?: string;
}

interface Store {
  id: string;
  name: string;
  logo: string;
  description: string;
  category: string;
  subcategory?: string;
  rating: number;
  isActive: boolean;
  createdAt: string;
  promos: StorePromo[];
}

interface TopDeal extends StorePromo {
  storeName: string;
  storeCategory: string;
}

export default function TopDeals() {
  const navigate = useNavigate();
  const [revealedCodes, setRevealedCodes] = useState<Set<string>>(new Set());
  const [topDeals, setTopDeals] = useState<TopDeal[]>([]);

  useEffect(() => {
    // Загружаем магазины из localStorage
    const stores = readPersistedStores(localStorage) as Store[];
    
    // Собираем все топ-акции из активных магазинов
    const deals: TopDeal[] = [];
    
    stores.forEach(store => {
      if (store.isActive) {
        store.promos.forEach(promo => {
          if (promo.isActive && promo.isTopDeal) {
            deals.push({
              ...promo,
              storeName: store.name,
              storeCategory: store.category
            });
          }
        });
      }
    });

    // Сортируем по дате создания (новые первыми) и берём топ-3
    setTopDeals(deals.slice(0, 3));
  }, []);

  const handleGetDeal = (dealId: string, code: string, partnerUrl?: string) => {
    // Показываем промокод
    setRevealedCodes(prev => new Set([...prev, dealId]));
    
    // Копируем код в буфер обмена
    if (code) {
      navigator.clipboard.writeText(code);
    }

    // Трекинг
    const deal = topDeals.find(d => d.id === dealId);
    if (deal) {
      const trackData = {
        promoId: dealId,
        promoCode: code,
        promoTitle: deal.description,
        storeName: deal.storeName,
        storeId: dealId,
        discount: deal.discount,
      };
      trackPromoReveal(trackData);
      trackPromoCopy(trackData);
    }

    openPartnerLink(partnerUrl);
  };

  const isCodeRevealed = (dealId: string) => revealedCodes.has(dealId);

  if (topDeals.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-gradient-to-br from-blue-50 to-orange-50">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Топ акции недели</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Самые выгодные предложения с максимальными скидками
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {topDeals.map((deal, index) => (
            <div
              key={deal.id}
              className={`bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 ${
                index === 0 ? 'lg:col-span-2 lg:row-span-1' : ''
              }`}
            >
              {deal.image && (
                <div className="relative">
                  <img
                    src={deal.image}
                    alt={deal.description}
                    className="w-full h-48 object-cover object-top"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                      {getPromoBadgeLabel(deal.discount)}
                    </span>
                  </div>
                </div>
              )}

              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-blue-600">{deal.storeName}</span>
                  <span className="text-xs text-gray-500">{deal.storeCategory}</span>
                </div>

                <h3 className="font-bold text-gray-900 mb-2 text-lg">{deal.description}</h3>

                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-gray-500">
                    Действует до {deal.expiryDate}
                  </span>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                    <div className="flex items-center justify-between">
                      <span className={`font-mono font-bold text-gray-900 ${!isCodeRevealed(deal.id) ? 'filter blur-sm select-none' : ''}`}>
                        {deal.code}
                      </span>
                      {isCodeRevealed(deal.id) && (
                        <button
                          onClick={() => navigator.clipboard.writeText(deal.code)}
                          className="text-blue-600 hover:text-blue-700 cursor-pointer"
                        >
                          <i className="ri-file-copy-line"></i>
                        </button>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleGetDeal(deal.id, deal.code, deal.partnerUrl)}
                    className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap font-semibold"
                  >
                    {isCodeRevealed(deal.id) ? 'Получено!' : getPromoActionLabel(deal.code)}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/top-deals')}
            className="bg-blue-600 text-white px-8 py-3 rounded-full hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            Все топ акции
          </button>
        </div>
      </div>
    </section>
  );
}
